<template>
  <div>
    <AddUserToSequence
      :selected-user-id="selectedUserId"
      v-model="isAddUserToSequenceDialogVisible"
      @clearSelectedUserId="clearSelectedUser()" />

    <ChangePasswordUser
      :user="selectedUserId"
      @clearSelectedUser="clearSelectedUser()"
      v-model="showChangePasswordUserDialog"
    />

    <OneTimePassword v-model="showOneTimePasswordDialog"/>

    <SetResponsible
      :user="selectedUserId"
      @clearSelectedUser="clearSelectedUser()"
      v-model="showSetResponsibleDialog"
    />

    <DeleteUser
      :user="selectedUserId"
      @clearSelectedUser="clearSelectedUser()"
      @getUsers="getUsers()"
      v-model="showDeleteUserDialog"
    />

    <view-user
      :auths="authsSelectedUser"
      customComponent="Profile"
      :user-id="selectedUserId"
      v-model="viewUserDialog"
      @clearUserIdAndAuths="clearUserIdAndAuths()"
      @getUsers="getUsers()"
    />

    <v-data-table
      fixed-header
      :headers="tableHeaders"
      :height="dataTableHeight"
      :items="users"
      item-key="userId"
      :loading="loadingUsers"
      :options.sync="options"
      show-select
      :footer-props="{
            'items-per-page-text': `${$t('general.people')} ${$t('general.perPage')}`,
            'items-per-page-options': itemsPerPageOptions
          }"
      :server-items-length="totalUsers"
      class="elevation-2 mt-4"
      :must-sort="false"
      v-model="selectedUsers"
      v-show="!isTableEditingMode">
      <template v-slot:item="{ item, index }">
        <tr class="kbDarkBlueText--text">
          <td>
            <div>
              <v-checkbox color="kbDarkBlueText"
                          :value="item"
                          v-model="selectedUsers"/>
            </div>
          </td>
          <td v-for="(header, headerIndex) in tableHeaders"
              :key="headerIndex">
            <div v-if="header.value === 'verified'" class="text-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-avatar :color="item[header.value] ? 'success' : 'error'"
                            size="10"
                            v-on="on"
                            style="cursor: pointer;"/>
                </template>
                <span v-if="item[header.value] && !item[header.value]">
                  {{ $t('general.invited') }}
                </span>
                <span v-else-if="!item[header.value]">
                  {{ item[header.value] ? $t('general.verified') : $t('general.notVerified') }}
                </span>
              </v-tooltip>
            </div>
            <div v-else-if="header.value === 'fullName'" class="font-weight-bold text-capitalize">
              <span class="d-block">{{ item[header.value] }}</span>
              <v-chip
                v-if="item.journeyId"
                class="ma-2"
                :color="item.journeyColor"
                label
                text-color="white"
                x-small>
                {{ item.journeyName }}
              </v-chip>
              <v-tooltip bottom v-for="(task, taskIndex) in item.tasks" :key="taskIndex">
                <template v-slot:activator="{ on }" v-if="item.tasks.length > 0 && taskIndex <= 2">
                  <v-chip :color="colorBasedOnDiff(task.diff)"
                          text-color="white"
                          x-small
                          class="pa-3 ma-1"
                          label
                          v-on="on"
                          style="cursor: pointer">
                    <v-avatar left>
                      <v-icon small>mdi-ballot-outline</v-icon>
                    </v-avatar>
                    {{ new Date(task.date).toLocaleString('default', { month: 'short' }).toUpperCase() + ' ' +
                    new Date(task.date).getDate() }}
                  </v-chip>
                </template>
                <span>{{ task.name }}</span>
              </v-tooltip>
            </div>

            <div v-else-if="header.value === 'name'" class="font-weight-bold text-capitalize">
              <InlineEditor field-type="text"
                            :input-rules="[!item.email && !item.phone ? rules.required : true]"
                            :value="item[header.value]"
                            @onSave="updateUserInline({ key: 'name', value: $event, UUID: item.UUID }, index)"
              >
                <span>{{ item[header.value] }}</span>
              </InlineEditor>
            </div>

            <div v-else-if="header.value === 'lastName'" class="font-weight-bold text-capitalize" style="min-width: 100px">
              <InlineEditor field-type="text"
                            :value="item[header.value]"
                            @onSave="updateUserInline({ key: 'lastName', value: $event, UUID: item.UUID }, index)"
              >
                <span>{{ item[header.value] }}</span>
              </InlineEditor>
            </div>

            <div v-else-if="header.value === 'auths'" style="min-width: 130px; text-align: center">
              <v-tooltip bottom v-if="item.isImported">
                <template v-slot:activator="{ on }">
                  <v-icon
                    class="mx-1"
                    color="kbDarkBlueText"
                    style="cursor: pointer;"
                    v-on="on"
                  >cloud_upload</v-icon>
                </template>
                <span>{{ $t('general.imported') }}</span>
              </v-tooltip>
              <v-icon
                class="mx-1"
                color="kbDarkBlueText"
                v-else-if="!item.auths || !Array.isArray(item.auths) || !item.auths.length"
              >
                {{item.phone ? 'phone' : 'mdi-email'}}
              </v-icon>
              <template v-if="item.auths && Array.isArray(item.auths)">
                <v-icon
                  :key="authIndex"
                  class="mx-1"
                  color="kbDarkBlueText"
                  v-for="(authItem, authIndex) in item.auths"
                >
                  {{ authItem | authIconFilter }}
                </v-icon>
              </template>
            </div>

            <div v-else-if="header.value === 'UUID'"   style="min-width: 130px; text-align: center">
              {{item[header.value]}}
            </div>

            <div v-else-if="header.value === 'email'"
                 :class="{ 'd-flex align-center': !item.emailValidationResult }"
                 style="min-width: 130px">
              <InlineEditor field-type="email"
                            :input-rules="[!item.name && !item.phone ? rules.required : true]"
                            :value="item[header.value]"
                            @onSave="updateUserInline({ key: 'email', value: $event, UUID: item.UUID }, index, true)">
                <span class="mr-1"
                      style="line-height: 1.2">
                  {{ item[header.value] }}
                </span>
              </InlineEditor>
              <EmailValidator :email="item[header.value]"
                              :email-validation-result="item.emailValidationResult"
                              :email-last-verified-at="item.emailLastVerifiedAt"
                              @onEmailValidated="getUsers()"/>
            </div>
            <div v-else-if="header.value === 'phone'" style="min-width: 80px;">
              <InlineEditor field-type="phone"
                            country-code-key="country"
                            number-key="countryCode"
                            :input-rules="[!item.name && !item.email ? rules.required : true]"
                            :value="selectedUserCountryPhone(item.phoneObj)"
                            @onSave="updateUserInline({ key: 'phone', value: $event, UUID: item.UUID }, index)">
                <div class="d-flex align-center">
                  <img style="margin-top: 5px"
                       class="iti-flag mr-1 mt-0"
                       :class="item.phoneObj.country.toLowerCase()"
                       v-if="item.phoneObj && item.phoneObj.country">
                  <span v-if="item.phoneObj && item.phoneObj.countryCode">
                    +{{ item.phoneObj.countryCode.toLowerCase() }}
                  </span>
                  <span class="pl-2">{{ item[header.value] }}</span>
                </div>
              </InlineEditor>
            </div>

            <div v-else-if="header.value === 'userRoles'" style="min-width: 80px;">
              <InlineEditor field-type="checkbox"
                            :options="filteredRolesOptions(item[header.value])"
                            option-value="id"
                            option-label="name"
                            :value="filteredRolesValues(item[header.value])"
                            @onSave="updateUserInline({ key: 'rolId', value: $event, UUID: item.UUID }, index)">
                <template>
                  <div v-if="item[header.value] && Array.isArray(item[header.value])">
                    <v-chip dark
                            v-for="(rol, index) in filteredRolesToShow(item[header.value])"
                            :key="index"
                            class="ma-1 kbPink--text"
                            color="kbPink"
                            outlined
                            small
                            pill>
                      <v-avatar left
                                class="kbPink kbWhite--text">
                        {{ index + 1 }}
                      </v-avatar>
                      {{ rol.name }}
                    </v-chip>
                    <span class="pl-1" v-if="item[header.value].length > 3">
                      + {{ item[header.value].length - 3 }}
                    </span>
                  </div>
                </template>
              </InlineEditor>
            </div>

            <div
              v-else-if="header.value === 'createdAt'"
              style="text-align: center"
            >
              {{ convertTZToUserTZ(item.createdAt) | moment('DD/MM/YYYY hh:mma') }}
            </div>
            <div v-else-if="header.value === 'actions'" class="ma-0 pa-0" style="text-align: center">
              <div class="d-flex justify-center align-center">
                <v-btn @click="showUser(item)" color="kbDarkBlueText" icon>
                  <v-icon size="27px">remove_red_eye</v-icon>
                </v-btn>
                <v-menu>
                  <template v-slot:activator="{ on }">
                    <v-btn color="kbDarkBlueText" text icon v-on="on">
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>

                  <v-list color="kbLight">
                    <v-list-item @click="showOneTimePassword(item)" v-if="!isCloudTask">
                      <v-list-item-action>
                        <v-icon color="kbDarkBlueText">looks_one</v-icon>
                      </v-list-item-action>
                      <v-list-item-title
                        class="kbDarkBlueText--text"
                      >{{ $t('general.oneTimePassword') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="showChangePasswordUser(item)" v-if="!isCloudTask">
                      <v-list-item-action>
                        <v-icon color="kbDarkBlueText">vpn_key</v-icon>
                      </v-list-item-action>
                      <v-list-item-title class="kbDarkBlueText--text">{{ $t('general.changePassword') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="showDeleteUser(item)">
                      <v-list-item-action>
                        <v-icon color="kbDarkBlueText">delete</v-icon>
                      </v-list-item-action>
                      <v-list-item-title class="kbDarkBlueText--text">{{ $t('general.deletePerson') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item class="kbDarkBlueText--text"
                                 @click="showAddUserToSequenceDialog(item.userId)">
                      <v-list-item-action class="mr-4">
                        <v-icon color="kbDarkBlueText">mdi-file-tree</v-icon>
                      </v-list-item-action>
                      <v-list-item-title class="kbDarkBlueText--text">
                        {{ $t('general.addUserToSequence') }}
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="showSetResponsible(item)" v-if="!isCloudTask">
                      <v-list-item-action>
                        <v-icon color="kbDarkBlueText">mdi-account</v-icon>
                      </v-list-item-action>
                      <v-list-item-title class="kbDarkBlueText--text">{{ $t('general.setResponsible') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
            <div v-else style="min-width: 130px;">
              <InlineEditor :field-type="header.type"
                            :options="header.options"
                            option-label="name"
                            option-value="name"
                            :value="item[header.value]"
                            @onSave="updateUserInline(
                              {
                                     value: $event,
                                     secondKey: header.text,
                                     type: header.type,
                                     UUID: item.UUID
                                    }, index)"
              >
                <span style="white-space: nowrap;overflow: hidden;text-overflow: ellipsis;max-width: 250px;">
                  {{ { value: item[header.value] , type: header.type } | extraDataAppearance(true)  }}
                </span>
              </InlineEditor>
            </div>
          </td>
        </tr>
      </template>

      <template v-slot:no-data>
        <v-row>
          <v-col
            class="text-center"
            v-if="loadingUsers"
            cols="12"
          >{{ $t('general.loading') }} {{ $t('general.people') }}</v-col>
          <v-col
            class="red--text text-center"
            v-else
            cols="12"
          >{{ $t('general.noPeopleRegistered') }}</v-col>
        </v-row>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import rules from '@/libs/rules'
import ChangePasswordUser from '@/pages/App/People/dialogs/ChangePasswordUser.vue'
import EmailValidator from '@/components/widgets/EmailValidator'
import DeleteUser from '@/pages/App/People/dialogs/DeleteUser.vue'
import OneTimePassword from '@/pages/App/People/dialogs/OneTimePassword.vue'
import SetResponsible from '@/pages/App/People/dialogs/SetResponsible.vue'
import Tasks from '@/mixins/Tasks'
import ViewUser from '@/pages/App/People/dialogs/ViewUser/ViewUser.vue'
import { mapState } from 'vuex'
import AddUserToSequence from '@/pages/App/People/dialogs/AddUserToSequence'
import ModuleTableHeight from '@/mixins/ModuleTableHeight'
import PeopleMixin from '@/mixins/PeopleMixin.vue'
import TimeZones from '@/mixins/TimeZones'
export default {
  components: {
    AddUserToSequence,
    ChangePasswordUser,
    DeleteUser,
    EmailValidator,
    OneTimePassword,
    SetResponsible,
    ViewUser
  },
  computed: {
    ...mapState({
      isCloudTask: state => state.auth.isCloudTask,
      isTableEditingMode: state => state.dataTables.isTableEditingMode,
      paginationUsers: state => state.users.paginationUsers,
      roles: state => state.roles.roles,
      totalUsers: state => state.users.totalUsers
    }),
    selectedUsers: {
      get () {
        return this.selectedUsersProp
      },
      set (value) {
        this.$emit('changeSelectedUsers', value)
      }
    },
    tableHeaders () {
      const actionsHeader = [{ text: this.$t('general.actions'), value: 'actions', align: 'center', sortable: false }]
      return [...actionsHeader, ...this.selectedPeopleDirectoryHeaders]
    }
  },
  data () {
    return {
      authsSelectedUser: [],
      isAddUserToSequenceDialogVisible: false,
      options: {},
      rules,
      selectedUserId: '',
      showChangePasswordUserDialog: false,
      showDeleteUserDialog: false,
      showOneTimePasswordDialog: false,
      showSetResponsibleDialog: false,
      viewUserDialog: false
    }
  },
  filters: {
    authIconFilter (authItem) {
      if (!authItem || typeof authItem !== 'object') return ''
      if (authItem.provider && authItem.provider.name) return `fab fa-${authItem.provider.name}`
    }
  },
  methods: {
    clearSelectedUser () {
      this.selectedUserId = null
    },
    clearSelectedUsers () {
      this.selectedUsers = []
    },
    clearUserIdAndAuths () {
      this.selectedUserId = ''
      this.authsSelectedUser = []
    },
    filteredRolesOptions (selectedUserRoles) {
      if (!selectedUserRoles || !Array.isArray(selectedUserRoles)) selectedUserRoles = []
      const foundPromotorInAllRoles = this.roles.find(item => item.name === 'promotor')
      const foundPromotorInUserRoles = selectedUserRoles.find(item => item.name === 'promotor')
      if (foundPromotorInAllRoles && foundPromotorInUserRoles) {
        return this.roles.filter(item => item.name !== 'promotor')
      }
      return this.roles
    },
    filteredRolesToShow (roles) {
      const max = roles.length < 3 ? roles.length : 3
      return roles.slice(0, max)
    },
    filteredRolesValues (roles) {
      if (!roles || !Array.isArray(roles)) return []
      return roles.map(rol => rol.id)
    },
    getUsers () {
      this.$store.dispatch('getUsers', { headers: this.selectedPeopleDirectoryHeaders })
    },
    selectedUserCountryPhone (phoneObject) {
      if (!phoneObject ||
        typeof phoneObject !== 'object' ||
        (typeof phoneObject === 'object' && Object.keys(phoneObject).length === 0)) return {}
      else {
        return {
          code: phoneObject.country || 'co',
          number: phoneObject.countryCode || '57',
          phone: phoneObject.phone || ''
        }
      }
    },
    setGlobalPagination () {
      this.$store.commit('SET_PAGINATION_USERS', {
        page: this.options.page,
        itemsPerPage: this.options.itemsPerPage,
        sortBy: this.options.sortBy[0],
        descending: this.options.sortDesc[0]
      })
    },
    setLocalPagination () {
      const { itemsPerPage, page, ...all } = this.options
      this.options = {
        itemsPerPage: this.paginationUsers.itemsPerPage,
        page: this.paginationUsers.page,
        ...all
      }
    },
    showAddUserToSequenceDialog (userId) {
      this.selectedUserId = userId
      this.isAddUserToSequenceDialogVisible = true
    },
    showChangePasswordUser (user) {
      this.selectedUserId = user.UUID || user.userId
      this.showChangePasswordUserDialog = true
    },
    showDeleteUser (user) {
      this.selectedUserId = user.UUID || user.userId
      this.showDeleteUserDialog = true
    },
    showSetResponsible (user) {
      this.selectedUserId = user.UUID || user.userId
      this.showSetResponsibleDialog = true
    },
    showOneTimePassword (user) {
      this.$store.dispatch('getUser', { userId: user.UUID })
      this.showOneTimePasswordDialog = true
    },
    showUser (user) {
      this.selectedUserId = user.UUID || user.userId
      this.authsSelectedUser = user.auths
      this.viewUserDialog = true
    },
    async updateUserInline (data, userIndex, getUsers = false) {
      await this.$emit('updateUserInline', { data, userIndex, getUsers })
    }
  },
  mixins: [ModuleTableHeight, PeopleMixin, Tasks, TimeZones],
  props: {
    itemsPerPageOptions: {
      type: Array,
      required: true
    },
    loadingUsers: {
      type: Boolean,
      required: true
    },
    selectedPeopleDirectoryHeaders: {
      type: Array,
      default: () => []
    },
    selectedUsersProp: {
      type: Array,
      required: true
    },
    users: {
      type: Array,
      required: true
    }
  },
  watch: {
    options: {
      handler () {
        if (!this.isTableEditingMode) this.setGlobalPagination()
      },
      deep: true
    },
    paginationUsers: {
      handler () {
        if (this.isTableEditingMode) this.setLocalPagination()
      },
      deep: true
    },
    users () {
      this.clearSelectedUsers()
    }
  }
}
</script>
